import { IAuthor } from '@models/IAuthor';
import { IListingComponent } from '@models/IListingComponent';
import AuthorCard from '@molecules/AuthorCard/AuthorCard';
import ListingResultsContainer from '@molecules/ListingResultsContainer/ListingResultsContainer';
import ListingTopBar from '@molecules/ListingTopBar/ListingTopBar';
import LoadMoreListing from '@molecules/LoadMoreListing/LoadMoreListing';
import useListingComponentProps from '@utils/hooks/useListingComponentProps';
import useRefocus from '@utils/hooks/useRefocus';

interface Props extends IListingComponent {
  readonly type: string[];
  readonly campaign: string[];
  readonly specialism: string[];
  readonly sectionTitle: string;
  readonly showExpandedFilters?: boolean;
  readonly showProfilePhotos?: boolean;
}

const AuthorTypeListings = (props: Props) => {
  const {
    campaign,
    componentName,
    showExpandedFilters,
    specialism,
    sectionTitle,
    showProfilePhotos = false,
    type,
  } = props;

  const preFilterParams = {
    AuthorType: type,
    GenreList: specialism,
    CampaignList: campaign,
  };

  const { results, ...listingProps } = useListingComponentProps({
    ...props,
    pageSize: 9,
    preFilterParams,
  });

  const placeholderRef = useRefocus(results);
  const placeIndex = listingProps.pageSize * listingProps.pageNumber;

  return (
    <section
      data-testid="authorTypeListingWrapper"
      data-name={componentName}
      className="component-padding bg-black-100 text-white"
    >
      <div className="container">
        {sectionTitle && (
          <h2 className="w-full pb-32 pt-[4.375rem] text-2xl lg:w-8/12 lg:pb-28 lg:pt-[8.75rem] lg:text-4xl">
            {sectionTitle}
          </h2>
        )}

        <ListingTopBar
          {...listingProps}
          pageType="AuthorListing"
          showExpandedFilters={showExpandedFilters}
        />
      </div>

      <ListingResultsContainer
        results={results}
        {...listingProps}
        resultsClassName="px-5 md:container"
        noResultsClassName="container"
        errorClassName="container"
      >
        <div className="mt-10 grid grid-cols-1 gap-x-4 gap-y-14 md:grid-cols-2 xl:grid-cols-3 xl:gap-y-36">
          {(results as IAuthor[]).map((author, index) => (
            <div
              tabIndex={-1}
              key={`${author.authorName}-${author.link?.url}`}
              ref={
                placeIndex > 0 && placeIndex === index ? placeholderRef : null
              }
            >
              <AuthorCard
                author={{
                  ...author,
                  initials: showProfilePhotos ? author.initials : undefined,
                  thumbnail: showProfilePhotos ? author.thumbnail : undefined,
                }}
              />
            </div>
          ))}
        </div>
      </ListingResultsContainer>

      {results?.length > 0 && (
        <div className="container flex justify-center pb-24 pt-8">
          <div className="py-4 text-lg">
            <LoadMoreListing results={results as IAuthor[]} {...listingProps} />
          </div>
        </div>
      )}
    </section>
  );
};

export default AuthorTypeListings;
